<template>
    <div class="flex-grow-1 d-flex flex-column" style="min-height: 0;">
        <!-- Slot for scroll container or other content -->
        <slot />
    </div>
</template>

<script>
import { timelineEditMixin } from '@/mixins/timelineEdit'
import TaskService from '@/services/task.service'
import taskStatsDialog from "@/components/TaskStatsDialog.vue"
import EditTextDialog from '@/components/EditTextDialog.vue'
import { parseJSON, differenceInSeconds } from 'date-fns'
import { LabelId, UserTaskType } from '@/store/modules/task'

const SECONDS_TO_MINUTES = 60

export default {
    name: "EventTimelineEditor",

    mixins: [timelineEditMixin],

    props: {
        tasks: {
            type: Array,
            required: true
        },
        event: {
            type: Object,
            required: true
        }
    },

    computed: {
        /**
         * Create a Map of tasks by ID for O(1) lookup performance
         * @returns {Map<number, Object>}
         */
        tasksById() {
            return new Map(this.tasks.map(task => [task.id, task]))
        }
    },

    mounted() {
        this.editMethods = this.editMethodsConfig
    },

    methods: {
        /**
         * Helper method to get siteId from event or route
         */
        getSiteId() {
            return this.event.site?.id || this.$route.params.siteId
        },

        /**
         * Centralized error handler
         * @param {Error} error - Error object
         * @param {string} userMessage - User-friendly error message
         */
        handleError(error, userMessage) {
            console.error(userMessage, error)
            this.$dialog.notify.error(userMessage)
        },

        /**
         * Find task by item.taskId and show error if not found
         */
        findTaskOrShowError(item) {
            const task = this.tasksById.get(item.taskId)

            if (!task) {
                this.handleError(
                    new Error(`Task not found: ${item.taskId}`),
                    'Nie można znaleźć zadania'
                )
                return null
            }

            return task
        },

        handleEdit(event) {
            const { item } = event

            if (item.type === 'message') {
                this.handleMessageEdit(item)
            } else {
                this.handleSimpleEdit(item)
            }
        },

        /**
         * Edit message-type timeline items with specific messages
         */
        handleMessageEdit(item) {
            this.handleStatusEntryEdit(item, null, {
                deleteSuccess: 'Usunięto komentarz',
                updateSuccess: 'Zaktualizowano komentarz',
                updateError: 'Błąd podczas aktualizacji komentarza'
            })
        },

        /**
         * Generic method for editing/deleting status entries
         * @param {Object} item - Timeline item
         * @param {Object} task - Task object (optional, will be found if null)
         * @param {Object} messages - Custom success/error messages
         */
        handleStatusEntryEdit(item, task = null, messages = {}) {
            const defaultMessages = {
                deleteSuccess: 'Usunięto pomyślnie',
                updateSuccess: 'Zaktualizowano pomyślnie',
                updateError: 'Błąd podczas aktualizacji'
            }
            const msg = { ...defaultMessages, ...messages }

            this.openEditDialog(item, async (item, result, isDelete) => {
                try {
                    const taskToUse = task || this.findTaskOrShowError(item)
                    if (!taskToUse) return

                    const entryId = item.source?.id
                    if (!entryId) {
                        throw new Error('Missing entry ID')
                    }

                    const siteId = this.getSiteId()
                    let response

                    if (isDelete) {
                        response = await TaskService.deleteStatusEntry(siteId, taskToUse.id, entryId)
                        this.$dialog.notify.success(msg.deleteSuccess)
                    } else {
                        const payload = {}
                        if (result.message !== undefined) payload.message = result.message
                        if (result.comment !== undefined) payload.comment = result.comment

                        response = await TaskService.editStatusEntry(siteId, taskToUse.id, entryId, payload)
                        this.$dialog.notify.success(msg.updateSuccess)
                    }

                    this.$emit('task-updated', response.data)
                } catch (error) {
                    this.handleError(error, msg.updateError)
                }
            })
        },

        handleSimpleEdit(item) {
            const task = this.findTaskOrShowError(item)
            if (!task) return

            if (this.isServiceCloseItem(item)) {
                this.updateOnSiteTime(item, task)
                return
            }

            if (this.isPatrolNotificationItem(item, task)) {
                this.handlePatrolNotificationEdit(item, task)
                return
            }

            if (item.source) {
                const itemId = item.source.id

                if (task.details?.found?.find(f => f.id === itemId)) {
                    this.handleFoundEdit(item, task)
                } else if (task.details?.notified?.find(n => n.id === itemId)) {
                    this.handleNotifiedEdit(item, task)
                } else {
                    this.handleGenericSimpleEdit(item, task)
                }
            } else {
                this.handleGenericSimpleEdit(item, task)
            }
        },

        isPatrolNotificationItem(item, task) {
            return task?.task_type === UserTaskType.PATROL && item.source?.label_id === LabelId.NEW
        },

        isServiceCloseItem(item) {
            return item?.taskType === UserTaskType.SERVICE && item?.source?.label_id === LabelId.CLOSE
        },

        async handlePatrolNotificationEdit(item, task) {
            try {
                const dialogInstance = await this.$dialog.show(EditTextDialog, {
                    title: item.title || 'Edycja opisu patrolu',
                    label: 'Szczegóły dla patrolu',
                    value: task.description || '',
                })

                dialogInstance.waitForResult = true
                const result = await dialogInstance.wait()
                if (result === false || result === undefined) return

                const updatedTask = {
                    ...task,
                    description: result,
                }

                const response = await TaskService.updateTask(this.getSiteId(), task.id, updatedTask)
                this.$emit('task-updated', response.data)
                this.$dialog.notify.success('Zaktualizowano opis patrolu')
            } catch (error) {
                this.handleError(error, 'Błąd podczas aktualizacji opisu patrolu')
            }
        },

        /**
         * Handle editing/deleting of 'found person' entries
         */
        handleFoundEdit(item, task) {
            this.$dialog.confirm({
                title: 'Edytuj osobę w obiekcie',
                text: `Czy chcesz usunąć osobę "${item.source.full_name}" z listy osób w obiekcie?`,
                actions: {
                    false: "Anuluj",
                    true: {
                        color: "error",
                        text: "Usuń"
                    }
                }
            }).then(async (confirmed) => {
                if (!confirmed) return

                try {
                    const siteId = this.getSiteId()
                    const response = await TaskService.deleteFoundUser(siteId, task.id, item.source)

                    this.$emit('task-updated', response.data)
                    this.$dialog.notify.success('Usunięto osobę z listy osób w obiekcie')
                } catch (error) {
                    this.handleError(error, 'Błąd podczas usuwania osoby z obiektu')
                }
            })
        },

        /**
         * Handle editing/deleting of 'notified person' entries
         */
        handleNotifiedEdit(item, task) {
            this.$dialog.confirm({
                title: 'Edytuj powiadomienie',
                text: `Czy chcesz usunąć "${item.source.full_name}" z listy powiadomionych?`,
                actions: {
                    false: "Anuluj",
                    true: {
                        color: "error",
                        text: "Usuń"
                    }
                }
            }).then(async (confirmed) => {
                if (!confirmed) return

                try {
                    const siteId = this.getSiteId()
                    const response = await TaskService.deleteNotifiedUser(siteId, task.id, item.source)

                    this.$emit('task-updated', response.data)
                    this.$dialog.notify.success('Usunięto z listy powiadomionych')
                } catch (error) {
                    this.handleError(error, 'Błąd podczas usuwania z listy powiadomionych')
                }
            })
        },

        handleGenericSimpleEdit(item, task) {
            this.handleStatusEntryEdit(item, task)
        },

        handleEditTravelTime(event) {
            const { item } = event;

            const task = this.findTaskOrShowError(item);
            if (!task) return

            this.updateTravelTime(item, task);
        },

        handleEditOnSiteTime(event) {
            const { item } = event;

            const task = this.findTaskOrShowError(item);
            if (!task) return

            this.updateOnSiteTime(item, task);
        },

        /**
         * Update travel time statistics
         * Uses dedicated stats endpoint instead of bulk task update
         */
        async updateTravelTime(item, task) {
            try {
                const dialogInstance = await this.$dialog.show(taskStatsDialog, {
                    date: item.source?.created_at || item.createdAt,
                    title: 'Podaj datę dojazdu',
                    label: this.$t('task.stats.travel.time')
                })
                dialogInstance.waitForResult = true

                const result = await dialogInstance.wait()
                if (!result) return

                const stats = task.details?.stats || {}
                const beginTime = parseJSON(stats.travel_started_at)
                if (!(beginTime instanceof Date) || isNaN(beginTime)) {
                    this.$dialog.notify.error("Nieprawidłowa data dojazdu")
                    return
                }

                const arrivalTime = new Date(result)
                const travelTime = differenceInSeconds(arrivalTime, beginTime)

                if (travelTime < 0) {
                    this.$dialog.notify.error("Nieprawidłowa data dojazdu")
                    return
                }

                // Update via dedicated stats endpoint
                const siteId = this.getSiteId()
                const updatedStats = {
                    ...stats,
                    travel_time: travelTime,
                    travel_finished_at: this.$options.filters.backendISO(arrivalTime)
                }

                const response = await TaskService.updateStats({
                    siteId,
                    taskId: task.id,
                    stats: updatedStats
                })

                // Update local timeline item for immediate visual feedback
                if (item.source) {
                    item.source.created_at = updatedStats.travel_finished_at
                }
                item.updateTravelTime(travelTime)

                this.$emit('task-updated', response.data)

                const travelTimeMinutes = Math.round(travelTime / SECONDS_TO_MINUTES)
                this.$dialog.notify.success(`Czas dojazdu zaktualizowany: ${travelTimeMinutes} min`)
            } catch (error) {
                this.handleError(error, 'Błąd podczas aktualizacji czasu dojazdu')
            }
        },

        async updateOnSiteTime(item, task) {
            try {
                const stats = task.details?.stats || {}
                const beginTime = parseJSON(stats.on_site_started_at)
                if (!(beginTime instanceof Date) || isNaN(beginTime)) {
                    this.$dialog.notify.error('Nieprawidłowa data rozpoczęcia serwisu')
                    return
                }

                const dialogInstance = await this.$dialog.show(taskStatsDialog, {
                    date: stats.on_site_finished_at || item.source?.created_at || item.createdAt,
                    title: 'Podaj datę zakończenia serwisu',
                    label: 'Czas serwisu'
                })
                dialogInstance.waitForResult = true

                const result = await dialogInstance.wait()
                if (!result) return

                const finishedAt = new Date(result)
                const onSiteTime = differenceInSeconds(finishedAt, beginTime)

                if (onSiteTime < 0) {
                    this.$dialog.notify.error('Nieprawidłowa data zakończenia serwisu')
                    return
                }

                const siteId = this.getSiteId()
                const response = await TaskService.updateStats({
                    siteId,
                    taskId: task.id,
                    stats: {
                        on_site_time: onSiteTime,
                        on_site_finished_at: this.$options.filters.backendISO(finishedAt)
                    }
                })

                if (item.source) {
                    item.source.created_at = this.$options.filters.backendISO(finishedAt)
                    item.createdAt = finishedAt
                }
                if (typeof item.updateOnSiteTime === 'function') {
                    item.updateOnSiteTime(onSiteTime)
                }

                this.$emit('task-updated', response.data)

                const onSiteTimeMinutes = Math.round(onSiteTime / SECONDS_TO_MINUTES)
                this.$dialog.notify.success(`Czas serwisu zaktualizowany: ${onSiteTimeMinutes} min`)
            } catch (error) {
                this.handleError(error, 'Błąd podczas aktualizacji czasu serwisu')
            }
        },

        /**
         * Handle deletion of timeline items
         * Uses deleteStatusEntry when entryId is available
         */
        handleDelete(event) {
            const { item } = event

            this.confirmDelete(item, async (item) => {
                try {
                    const task = this.findTaskOrShowError(item)
                    if (!task) return

                    const siteId = this.getSiteId()
                    const entryId = item.source?.id

                    let response
                    if (entryId) {
                        // Use dedicated endpoint when entry ID is available
                        response = await TaskService.deleteStatusEntry(siteId, task.id, entryId)
                    } else {
                        // Fallback to bulk update for items without entry ID
                        if (task.details?.history && item.source) {
                            task.details.history = task.details.history.filter(h => h !== item.source)
                        }
                        response = await TaskService.updateTask(siteId, task.id, task)
                    }

                    this.$emit('task-updated', response.data)
                    this.$dialog.notify.success('Usunięto pomyślnie')
                } catch (error) {
                    this.handleError(error, 'Błąd podczas usuwania')
                }
            })
        }
    }
}
</script>
